import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MagnifyingGlass, User, Phone, Envelope, Plus, FloppyDisk } from "@phosphor-icons/react";

export default function CustomersPage() {
  useDocumentTitle("Clienti");
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ surname: "", name: "", phone: "", phone2: "", email: "", email2: "", email3: "", pec: "", address: "", contratto: "", notes: "", ragione_sociale: "", piva: "", codice_fiscale: "", codice_sdi: "", iban: "", sede_legale: "" });

  const load = async (query = "") => {
    setLoading(true);
    const { data } = await api.get("/customers", { params: query ? { q: query } : {} });
    setCustomers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.phone.trim()) {
      return toast.error("Nome, cognome e telefono obbligatori");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim(),
        phone2: form.phone2.trim() || null,
        email: form.email.trim() || null,
        email2: form.email2.trim() || null,
        email3: form.email3.trim() || null,
        pec: form.pec.trim() || null,
        address: form.address.trim() || null,
        contratto: form.contratto.trim() || null,
        notes: form.notes.trim() || null,
        ragione_sociale: form.ragione_sociale.trim() || null,
        piva: form.piva.trim() || null,
        codice_fiscale: form.codice_fiscale.trim() || null,
        codice_sdi: form.codice_sdi.trim() || null,
        iban: form.iban.trim() || null,
        sede_legale: form.sede_legale.trim() || null,
      });
      toast.success("Cliente creato");
      setOpenCreate(false);
      setForm({ surname: "", name: "", phone: "", phone2: "", email: "", email2: "", email3: "", pec: "", address: "", contratto: "", notes: "", ragione_sociale: "", piva: "", codice_fiscale: "", codice_sdi: "", iban: "", sede_legale: "" });
      // Go straight to the new customer's page
      nav(`/clienti/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// ANAGRAFICA</div>
          <h1 className="text-4xl font-display font-bold tracking-tight">Clienti</h1>
          <p className="text-muted-foreground mt-1 text-sm">{customers.length} {customers.length === 1 ? "cliente" : "clienti"} totali</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="new-customer-btn" className="rounded-full gap-2">
          <Plus size={16} weight="bold" /> Nuovo cliente
        </Button>
      </header>

      <Card className="p-4 border-border/70 rounded-lg mb-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome, cognome, telefono…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="customers-search"
          />
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-muted-foreground py-8">Caricamento…</div>
      ) : customers.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">Nessun cliente</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <Link
              to={`/clienti/${c.id}`}
              key={c.id}
              data-testid={`customer-card-${c.id}`}
              className="block"
            >
              <Card className="p-5 border-border/70 rounded-lg hover:shadow-md hover:-translate-y-0.5 h-full"
                    style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center">
                    <User size={18} weight="duotone" />
                  </div>
                  <div>
                    <div className="font-medium">{c.surname} {c.name}</div>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone size={14} /> <span className="font-mono">{c.phone}</span>
                  </div>
                  {c.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Envelope size={14} /> <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="truncate text-primary hover:underline">{c.email}</a>
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Nuovo cliente dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-customer-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo cliente</DialogTitle>
            <DialogDescription>Compila i dati del cliente. Solo cognome, nome e telefono sono obbligatori.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Cognome *</Label>
                <Input required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="new-customer-surname" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Nome *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-customer-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Telefono *</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-customer-phone" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Telefono 2</Label>
                <Input value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} data-testid="new-customer-phone2" className="mt-1.5 font-mono" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-customer-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email 2</Label>
                <Input type="email" value={form.email2} onChange={(e) => setForm({ ...form, email2: e.target.value })} data-testid="new-customer-email2" className="mt-1.5" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">Email 3</Label>
                <Input type="email" value={form.email3} onChange={(e) => setForm({ ...form, email3: e.target.value })} data-testid="new-customer-email3" className="mt-1.5" placeholder="Opzionale" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest">PEC</Label>
                <Input type="email" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} data-testid="new-customer-pec" className="mt-1.5" placeholder="Opzionale" />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Indirizzo</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="new-customer-address" className="mt-1.5" />
            </div>

            <div className="border-t border-border/50 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">// Dati fatturazione (opzionali)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Ragione sociale</Label>
                  <Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} data-testid="new-customer-ragione" className="mt-1.5" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">P.IVA</Label>
                  <Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} data-testid="new-customer-piva" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Codice Fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} data-testid="new-customer-cf" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Codice SDI</Label>
                  <Input value={form.codice_sdi} onChange={(e) => setForm({ ...form, codice_sdi: e.target.value })} data-testid="new-customer-sdi" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest">IBAN</Label>
                  <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="new-customer-iban" className="mt-1.5 font-mono" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest">Sede legale</Label>
                  <Input value={form.sede_legale} onChange={(e) => setForm({ ...form, sede_legale: e.target.value })} data-testid="new-customer-sede" className="mt-1.5" />
                </div>
              </div>
            </div>

            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Contratto <span className="text-muted-foreground/70 normal-case">(opzionale)</span></Label>
              <Textarea rows={2} placeholder="Es. Contratto Assistenza n. 2026/001 · durata annuale · rinnovo tacito" value={form.contratto} onChange={(e) => setForm({ ...form, contratto: e.target.value })} data-testid="new-customer-contratto" className="mt-1.5" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Note</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-customer-notes" className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpenCreate(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="new-customer-submit" className="rounded-full gap-2">
                <FloppyDisk size={16} weight="bold" /> {saving ? "Salvo…" : "Crea cliente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
